import { Stack, Typography } from '@mui/material';
import { format, getISOWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { dateLocales } from '@/i18n/dateLocales';
import type { WeekTokens } from './weekTokens';

type WeekNavLabelProps = {
  /** Måndagen i den vecka som visas (barn/teen: alltid innevarande vecka). */
  weekStart: Date;
  weekEnd: Date;
  tokens: WeekTokens;
};

/**
 * WeekNavLabel — STATISK vecko-etikett för barn/teen. Fyller samma header-slot
 * som adults WeekSelect-kontroller, men UTAN bläddring (barn/teen är låsta till
 * innevarande vecka, beslut #3). Speglar WeekSelects typografi (år / månad /
 * "Vecka NN") så att navigeringsplatsen ser konsekvent ut över alla personas.
 */
const WeekNavLabel = ({ weekStart, weekEnd, tokens }: WeekNavLabelProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const locale = dateLocales[lang] ?? dateLocales.en;

  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();

  return (
    <Stack alignItems="center" sx={{ color: tokens.text }}>
      <Typography
        variant="subtitle2"
        sx={{
          textTransform: 'capitalize',
          lineHeight: 1,
          fontWeight: 'bold',
          color: tokens.accent,
        }}
      >
        {format(weekEnd, 'yyyy', { locale })}
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{ textTransform: 'capitalize', lineHeight: 1.25, color: tokens.textSoft }}
      >
        {sameMonth
          ? format(weekStart, 'MMMM', { locale })
          : `${format(weekStart, 'MMMM', { locale })} / ${format(weekEnd, 'MMMM', { locale })}`}
      </Typography>
      <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
        {t('Week') + ' ' + getISOWeek(weekStart).toString().padStart(2, '0')}
      </Typography>
    </Stack>
  );
};

export default WeekNavLabel;
